/**
 * Enum for credit app search slideout view states
 */
export enum CreditAppSearchView {
    SEARCH = 'SEARCH',
    REVIEW = 'REVIEW'
}

/**
 * Form data for credit app search
 */
export interface CreditAppSearchFormData {
    lastName: string;
    phone: string;
    email: string;
}

/**
 * Field-level validation errors
 */
export interface CreditAppSearchFormErrors {
    lastName?: string;
    phone?: string;
    email?: string;
}

/**
 * Tracks which fields have been blurred (touched) for validation
 */
export interface CreditAppSearchFormTouched {
    lastName: boolean;
    phone: boolean;
    email: boolean;
}

/**
 * Search result interface - transformed for display in UI
 * Represents a credit application from search results
 */
export interface CreditAppSearchResult {
    creditAppId: string;
    dealType: 'Finance Deal' | 'Lease Deal' | 'Cash Deal';
    createdDate: string;
    customerName: string;
    phone: string;
    email: string;
    vehicleYear: string;
    vehicleMake: string;
    vehicleModel: string;
    vehicleTrim?: string;
    vin: string;
    stockNumber: string;
    rawDeal: any; // Use 'any' to avoid circular dependency - actual type is IDealSearchDeal from shared-bff-types
}

/**
 * Search error state
 * - hasError: true indicates a network/server error (shows service unavailable message)
 * - hasNoResults: true indicates successful search with no matches (shows "No Results" message)
 */
export interface SearchError {
    hasError: boolean;
    hasNoResults: boolean;
}

/**
 * Deal data interface for current and new deal comparison
 * Used in review view to display buyer and co-buyer information
 * This is not the Deal API schema
 * All string fields are required and default to empty string when undefined/null
 */
export interface DealData {
    buyer: {
        firstName: string;
        middleName: string;
        lastName: string;
        emailAddress: string;
        phoneNumber: string;
        address: {
            street: string;
            city: string;
            state: string;
            zip: string;
        };
    };
    coBuyer?: {
        firstName: string;
        middleName: string;
        lastName: string;
        emailAddress: string;
        phoneNumber: string;
        address: {
            street: string;
            city: string;
            state: string;
            zip: string;
        };
    };
}

/**
 * Return type for useCreditAppSearchForm hook
 */
export interface UseCreditAppSearchFormReturn {
    formData: CreditAppSearchFormData;
    errors: CreditAppSearchFormErrors;
    touched: CreditAppSearchFormTouched;
    handleFieldChange: (field: keyof CreditAppSearchFormData, value: string) => void;
    handleFieldBlur: (field: keyof CreditAppSearchFormData) => void;
    clearForm: () => void;
    isSearchButtonEnabled: boolean;
}

/**
 * Return type for useSearchCreditApps hook
 */
export interface UseSearchCreditAppsReturn {
    searchResults: CreditAppSearchResult[];
    isLoading: boolean;
    error: SearchError | null;
    performSearch: (criteria: CreditAppSearchFormData) => Promise<void>;
    clearResults: () => void;
    cancelSearch: () => void;
}

/**
 * Props for SearchView component
 */
export interface SearchViewProps {
    formData: CreditAppSearchFormData;
    errors: CreditAppSearchFormErrors;
    touched: CreditAppSearchFormTouched;
    isSearchButtonEnabled: boolean;
    onFieldChange: (field: keyof CreditAppSearchFormData, value: string) => void;
    onFieldBlur: (field: keyof CreditAppSearchFormData) => void;
    onClear: () => void;
    onSearch: () => void;
    description?: string;
}

/**
 * Props for SearchResultsView component
 */
export interface SearchResultsViewProps {
    results: CreditAppSearchResult[];
    isLoading: boolean;
    error: SearchError | null;
    onAttach: (creditAppId: string) => void;
    isLocked?: boolean;
}

export interface SelectableResultsViewProps {
    results: CreditAppSearchResult[];
    error: SearchError | null;
    selectedResultId: string | null;
    onSelect: (resultId: string) => void;
}

/**
 * Props for ReviewView component
 */
export interface ReviewViewProps {
    currentDealData: DealData;
    newDealData: DealData;
}
